import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Paperclip } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, post } from '../../nucleo/api';
import { comMaisOpcoes } from '../../nucleo/modo-simples';
import { useSessao } from '../../nucleo/sessao';
import { caminhoDaCategoria, categoriaLancavel, useCategorias, useCentros, useContas } from './dados';
import { hojeLocal, invalidarFinanceiro, type TipoLancamento } from './tipos';

export interface InicialLancamento {
  descricao?: string;
  valor_centavos?: number;
  data_caixa?: string;
  data_competencia?: string;
  conta_id?: string;
  parceiro_id?: string;
}

/**
 * Drawer de novo lançamento (M4.3): entrada ou saída, com comprovante anexo e categoria que combina com o tipo.
 * O id do lançamento nasce aqui e vale como Idempotency-Key: reenviar o mesmo formulário não duplica.
 */
export function FormLancamento({ aberto, tipo, inicial, aoFechar, aoSalvar }: { aberto: boolean; tipo: Exclude<TipoLancamento, 'transferencia'>; inicial?: InicialLancamento; aoFechar: () => void; aoSalvar?: (id: string) => void | Promise<void> }) {
  if (!aberto) return null;
  return <Corpo key={`${tipo}-${inicial?.descricao ?? ''}`} tipo={tipo} inicial={inicial} aoFechar={aoFechar} aoSalvar={aoSalvar} />;
}

function Corpo({ tipo, inicial, aoFechar, aoSalvar }: { tipo: Exclude<TipoLancamento, 'transferencia'>; inicial?: InicialLancamento; aoFechar: () => void; aoSalvar?: (id: string) => void | Promise<void> }) {
  const { t } = useTranslation('financeiro');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const s = useSessao();
  const contas = useContas(true);
  const categorias = useCategorias();
  const centros = useCentros();
  const [novoId] = useState(() => crypto.randomUUID());
  const [arquivo, setArquivo] = useState<File | null>(null);
  const anexoEnviado = useRef<string | null>(null);
  const seletor = useRef<HTMLInputElement>(null);

  const padrao = contas.data?.itens.find((c) => c.padrao)?.id ?? contas.data?.itens[0]?.id;
  const tipoCategoria = tipo === 'entrada' ? 'receita' : 'despesa';
  const todas = categorias.data?.itens ?? [];
  // Só categorias analíticas, folha e ativas do tipo certo (o pai e a sintética agrupam, o lançamento vai na filha).
  const opcoesCategoria = todas
    .filter((c) => categoriaLancavel(c, todas, tipoCategoria))
    .map((c) => ({ valor: c.id, rotulo: caminhoDaCategoria(c, todas) }));

  const campos: DefCampo[] = [
    { nome: 'descricao', rotulo: t('campos.descricao'), tipo: 'texto', obrigatorio: true },
    { nome: 'valor_centavos', rotulo: t('campos.valor'), tipo: 'dinheiro', obrigatorio: true, largura: 'meia' },
    { nome: 'conta_id', rotulo: t('campos.conta'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: (contas.data?.itens ?? []).map((c) => ({ valor: c.id, rotulo: c.nome })) },
    { nome: 'data_caixa', rotulo: t('campos.dataCaixa'), tipo: 'data', obrigatorio: true, largura: 'meia', ajuda: t('ajuda.dataCaixa') },
    { nome: 'data_competencia', rotulo: t('campos.dataCompetencia'), tipo: 'data', obrigatorio: true, largura: 'meia', ajuda: t('ajuda.dataCompetencia') },
    { nome: 'categoria_id', rotulo: t('campos.categoria'), tipo: 'selecao', largura: 'meia', opcoes: opcoesCategoria, ajuda: tipo === 'entrada' ? t('ajuda.categoriaReceita') : t('ajuda.categoriaDespesa') },
    { nome: 'centro_custo_id', rotulo: t('campos.centroCusto'), tipo: 'selecao', largura: 'meia', opcoes: (centros.data?.itens ?? []).filter((c) => c.ativo).map((c) => ({ valor: c.id, rotulo: c.nome })) },
    { nome: 'parceiro_id', rotulo: t('campos.parceiro'), tipo: 'busca', busca: { caminho: '/cadastros/parceiros', params: { operacao: true }, rotulo: (i) => String(i.nome) } },
  ];

  const hoje = hojeLocal();
  const valoresIniciais: Record<string, unknown> = {
    data_caixa: inicial?.data_caixa ?? hoje,
    data_competencia: inicial?.data_competencia ?? inicial?.data_caixa ?? hoje,
    conta_id: inicial?.conta_id ?? padrao,
    descricao: inicial?.descricao,
    valor_centavos: inicial?.valor_centavos,
    parceiro_id: inicial?.parceiro_id,
  };
  // U15 F5: no modo simples, a conta (a padrão do workspace), o mês de referência (que nasce igual à data de caixa), o centro de
  // custo e o cliente ou fornecedor ficam em "Mais opções". Só sai da vista o que tem valor padrão seguro: sem conta padrão,
  // a conta (obrigatória) continua à vista (`comMaisOpcoes` não esconde obrigatório sem valor).
  const maisOpcoes = comMaisOpcoes(campos, { ativo: s.modoSimples, avancados: ['conta_id', 'data_competencia', 'centro_custo_id', 'parceiro_id'], rotulo: tn('comum.maisOpcoes'), inicial: valoresIniciais });
  const carregando = contas.isPending || categorias.isPending || centros.isPending;
  const falha = contas.isError ? contas : categorias.isError ? categorias : centros.isError ? centros : null;

  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      titulo={tipo === 'entrada' ? t('acoes.novaEntrada') : t('acoes.novaSaida')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-lancamento" ocupado={carregando || !!falha}>
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      {falha ? (
        <Erro erro={falha.error} aoTentar={() => void Promise.all([contas.refetch(), categorias.refetch(), centros.refetch()])} />
      ) : carregando ? (
        <Carregando />
      ) : (
        <Formulario
          id="form-lancamento"
          campos={maisOpcoes.campos}
          gruposRecolhiveis={maisOpcoes.gruposRecolhiveis}
          recolhiveisAoFim
          entidade="financeiro.lancamento"
          inicial={valoresIniciais}
          aoEnviar={async (v) => {
            if (arquivo && !anexoEnviado.current) {
              const fd = new FormData();
              fd.append('entidade', 'financeiro.lancamento');
              fd.append('registro_id', novoId);
              fd.append('arquivo', arquivo);
              anexoEnviado.current = (await api<{ id: string }>('POST', '/anexos', fd)).id;
            }
            await post(
              '/financeiro/lancamentos',
              {
                id: novoId,
                tipo,
                descricao: v.descricao,
                valor_centavos: v.valor_centavos,
                conta_id: v.conta_id || null,
                data_caixa: v.data_caixa,
                // Modo simples: o mês de referência escondido que a pessoa não mexeu acompanha a data de caixa que ela escolheu.
                data_competencia: s.modoSimples && v.data_competencia === valoresIniciais.data_competencia ? v.data_caixa : v.data_competencia,
                categoria_id: v.categoria_id || null,
                centro_custo_id: v.centro_custo_id || null,
                parceiro_id: v.parceiro_id || null,
                comprovante_anexo_id: anexoEnviado.current,
                extras: v.extras ?? {},
              },
              { 'Idempotency-Key': novoId },
            );
            await aoSalvar?.(novoId);
            await invalidarFinanceiro(qc);
            toast.sucesso(tn('comum.salvo'));
            aoFechar();
          }}
        >
          <div className="flex flex-col gap-1.5 sm:col-span-6">
            <span className="text-sm font-medium text-tinta-2">{t('campos.comprovante')}</span>
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <Botao icone={<Paperclip className="size-4" />} onClick={() => seletor.current?.click()}>
                {arquivo ? t('acoes.trocarArquivo') : t('acoes.anexarComprovante')}
              </Botao>
              {arquivo && <span className="min-w-0 truncate text-sm text-tinta-2" title={arquivo.name}>{arquivo.name}</span>}
              <input ref={seletor} type="file" className="sr-only" tabIndex={-1} aria-label={t('campos.comprovante')} title={t('campos.comprovante')} accept="image/*,.pdf" onChange={(e) => setArquivo(e.target.files?.[0] ?? null)} />
            </div>
          </div>
        </Formulario>
      )}
    </Drawer>
  );
}
